import * as Yup from 'yup';
import { Box, Button, Paper, Stack, TableCell, TableRow, Tooltip } from '@mui/material';
import { CheckmarkIcon, CloseIcon, CommentNoteIcon, PieChartIcon } from '@synergy/theme-next';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { PaloTableViewport } from '@synergy/components-palo';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import {
    PoolCheckbox,
    PoolConfirmationModal,
    PoolFilterSearchBar,
    PoolTooltip,
    PoolVirtualTable,
} from '@synergy/components-next';
import { PoolTextArea } from '@synergy/components';
import { errorsToTouch } from '@synergy/utils';
import { useMemo, useState } from 'react';

import { DemandAccountParticipant } from '../../../models/demand-account-participant';
import { DemandAccountStatus } from '../../../models/demand-account-status';
import { DemandDetailFeedbackActionMenu } from '../pages/feedback-detail/components/feedback-action-menu';
import { DemandSteps } from '../models/demand-steps';
import { FeedbackDetailFormModal } from '../pages/feedback-detail/feedback-detail-form-modal';
import { FormikGroupItem } from '../../../../../common/components/new-stepper/formik-group-item';
import { decline, sendInfoMail, submitFeedback } from '../store/feedback/demand-detail-feedback-actions';
import {
    demandDetailFeedbackBaselineSelector,
    demandDetailFeedbackDataSelector,
} from '../store/feedback/demand-detail-feedback-selectors';
import { demandDetailOverviewDataSelector } from '../store/overview/demand-detail-overview-selectors';
import { demandDetailStepperStateSelector } from '../store/demand-detail-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const icons = {
    [DemandAccountStatus.Accepted]: (
        <Tooltip title={<FormattedMessage id="app.demands.detail.feedback.status.accepted" />} placement="top-start">
            <div>
                <CheckmarkIcon color="success" />
            </div>
        </Tooltip>
    ),

    [DemandAccountStatus.PartiallyAccepted]: (
        <Tooltip
            title={<FormattedMessage id="app.demands.detail.feedback.status.partiallyAccepted" />}
            placement="top-start"
        >
            <div>
                <PieChartIcon color="success" />
            </div>
        </Tooltip>
    ),

    [DemandAccountStatus.Declined]: (
        <Tooltip title={<FormattedMessage id="app.demands.detail.feedback.status.declined" />} placement="top-start">
            <div>
                <CloseIcon color="error" />
            </div>
        </Tooltip>
    ),

    [DemandAccountStatus.Created]: (
        <Tooltip title={<FormattedMessage id="app.demands.detail.feedback.status.pending" />} placement="top-start">
            <div>
                <CheckmarkIcon sx={{ color: '#E7E7E7' }} />
            </div>
        </Tooltip>
    ),
};

const validationSchema = Yup.object().shape({});

type PaloDemandDetailFeedbackProps = {
    demandId?: number;
};

export const PaloDemandDetailFeedback = ({ demandId }: PaloDemandDetailFeedbackProps): JSX.Element => {
    const dispatch = useThunkDispatch();
    const feedbackData = useAppSelector(demandDetailFeedbackDataSelector);
    // The step as the server has it — see FormikGroupItem.dirtyBaseline.
    const baseline = useAppSelector(demandDetailFeedbackBaselineSelector);
    const overviewData = useAppSelector(demandDetailOverviewDataSelector);
    const readonly = useAppSelector(demandDetailStepperStateSelector).readonlySteps.includes(DemandSteps.Feedback);
    const [declineDemandAccountsIds, setDeclineDemandAccountsIds] = useState<number[]>([]);
    const [isWarningDialogVisible, setIsWarningDialogVisible] = useState(false);
    const [isReasonDialogVisible, setIsReasonDialogVisible] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [detailModalDemandAccount, setDetailModalDemandAccount] = useState<DemandAccountParticipant | undefined>(
        undefined
    );
    const [searchTerm, setSearchTerm] = useState('');

    const participants = useMemo(
        () =>
            feedbackData.values.participants.filter(
                participant =>
                    participant.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    participant.contact.fullName.toLowerCase().includes(searchTerm.toLowerCase())
            ),
        [feedbackData.values.participants, searchTerm]
    );

    const handleSingleDecline = (id: number) => () => {
        setDeclineDemandAccountsIds([id]);
        setIsReasonDialogVisible(true);
    };

    const handleDecline = () => {
        dispatch(decline(declineDemandAccountsIds, declineReason));
        setSelectedIds(selectedIds.filter(selectedId => !declineDemandAccountsIds.includes(selectedId)));
        setDeclineDemandAccountsIds([]);
        setDeclineReason('');
        setIsWarningDialogVisible(false);
        setIsReasonDialogVisible(false);
    };

    const handleSendInfoMail = (id: number, demandAccountId: number) => () => {
        dispatch(sendInfoMail(id, demandAccountId));
    };

    const handleMultiDeclineClick = () => {
        setDeclineDemandAccountsIds(selectedIds);
        if (selectedIds.length === 1) {
            setIsReasonDialogVisible(true);
        } else {
            setIsWarningDialogVisible(true);
        }
    };

    const handleCheckChange = (id: number, checked: boolean) => {
        if (checked) {
            setSelectedIds([...selectedIds, id]);
        } else {
            setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
        }
    };

    const handleCheckAllChange = (checked: boolean) => {
        participants.forEach(
            participant => participant.status !== DemandAccountStatus.Declined && (participant.isSelected = checked)
        );
        setSelectedIds(checked ? participants.map(participant => participant.id) : []);
    };

    const allSelected = participants.every(
        participant => selectedIds.includes(participant.id) || participant.status === DemandAccountStatus.Declined
    );

    return (
        <PaloThemeProvider>
            <Stack sx={{ overflow: 'hidden', height: '100%', pb: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '10.5px',
                        fontWeight: 640,
                        letterSpacing: '.08em',
                        textTransform: 'uppercase',
                        color: tokens.color.ink3,
                        pb: '5px',
                        mb: '10px',
                        borderBottom: `1px solid ${tokens.color.line}`,
                    }}
                >
                    <FormattedMessage id="app.demands.detail.manageFeedback" />
                    <Box sx={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0 }}>
                        <PoolFilterSearchBar value={searchTerm} onChange={setSearchTerm} size="small" />
                    </Box>
                </Box>
                <FormikGroupItem
                    groupItemId={DemandSteps.Feedback}
                    dirtyBaseline={baseline}
                    initialValues={feedbackData.values}
                    onSubmit={data => dispatch(submitFeedback(data))}
                    validationSchema={validationSchema}
                    initialErrors={feedbackData.errors}
                    initialTouched={errorsToTouch(feedbackData.errors)}
                    enableReinitialize={true}
                    isInitialValid={feedbackData.isValid}
                >
                    <PaloTableViewport rows={participants.length}>
                        <Paper elevation={0} sx={{ height: '100%' }}>
                            <PoolVirtualTable
                                data={participants}
                                fixedHeaderContent={() => (
                                    <TableRow sx={{ background: tokens.color.surface }}>
                                        {/* width of the checkbox plus padding*/}
                                        <TableCell sx={{ width: 74 }}>
                                            <PoolCheckbox
                                                variant="selectAllRound"
                                                checked={allSelected}
                                                indeterminate={!allSelected && selectedIds.length > 0}
                                                onChange={(_, checked) => handleCheckAllChange(checked)}
                                                disabled={readonly}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ width: 74 }}>
                                            <FormattedMessage id="app.table.column.status" />
                                        </TableCell>
                                        <TableCell>
                                            <FormattedMessage id="app.table.column.customer" />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', width: 150 }}>
                                            <FormattedMessage id={'app.table.column.declineReason'} />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', width: 150 }}>
                                            <FormattedMessage id="app.table.column.updatedAt" />
                                        </TableCell>
                                        <TableCell sx={{ width: 74 }} />
                                    </TableRow>
                                )}
                                rowRenderer={participant => (
                                    <>
                                        <TableCell>
                                            {participant.status !== DemandAccountStatus.Declined && !readonly && (
                                                <PoolCheckbox
                                                    variant="round"
                                                    checked={selectedIds.includes(participant.id)}
                                                    onChange={(_, checked) =>
                                                        handleCheckChange(participant.id, checked)
                                                    }
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>{icons[participant.status]}</TableCell>
                                        <TableCell>
                                            <div>
                                                {participant.customer.name}
                                                <br />
                                                <small>{participant.contact.fullName}</small>
                                            </div>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            {participant.declineReason &&
                                                participant.status === DemandAccountStatus.Declined && (
                                                    <PoolTooltip
                                                        variant="light"
                                                        title={
                                                            <Stack gap={0.5}>
                                                                <Box typography="body1">
                                                                    <strong>
                                                                        <FormattedMessage id="app.demands.detail.feedback.declineReason" />
                                                                    </strong>
                                                                </Box>
                                                                <Box>
                                                                    <strong>{participant.customer.name}</strong>
                                                                    <br />
                                                                    {participant.declineReason}
                                                                </Box>
                                                            </Stack>
                                                        }
                                                    >
                                                        <CommentNoteIcon />
                                                    </PoolTooltip>
                                                )}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            <FormattedDate value={participant.emailSentDate} />
                                        </TableCell>
                                        <TableCell>
                                            {demandId && (
                                                <DemandDetailFeedbackActionMenu
                                                    readonly={readonly}
                                                    demandStatus={overviewData.values.status}
                                                    demandAccountStatus={participant.status}
                                                    onSendInfoMail={handleSendInfoMail(demandId, participant.id)}
                                                    onDeclineFeedback={handleSingleDecline(participant.id)}
                                                    onEditFeedback={() => setDetailModalDemandAccount(participant)}
                                                />
                                            )}
                                        </TableCell>
                                    </>
                                )}
                            />
                        </Paper>
                    </PaloTableViewport>

                    {!readonly && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled={selectedIds.length === 0 || readonly}
                                onClick={handleMultiDeclineClick}
                            >
                                <FormattedMessage id="app.demands.detail.feedback.decline" />
                            </Button>
                        </Box>
                    )}

                    <FeedbackDetailFormModal
                        demandAccountParticipant={detailModalDemandAccount}
                        onClose={() => setDetailModalDemandAccount(undefined)}
                    />

                    <PoolConfirmationModal
                        open={isWarningDialogVisible}
                        title={<FormattedMessage id="app.demands.detail.feedback.decline" />}
                        onCancel={() => setIsWarningDialogVisible(false)}
                        onConfirm={handleDecline}
                    >
                        <FormattedMessage id="app.demands.detail.feedback.declineWarning" />
                    </PoolConfirmationModal>

                    <PoolConfirmationModal
                        open={isReasonDialogVisible}
                        title={<FormattedMessage id="app.demands.detail.feedback.decline" />}
                        action={<FormattedMessage id="app.demands.detail.feedback.submit" />}
                        isSuccessDialog={true}
                        onCancel={() => setIsReasonDialogVisible(false)}
                        onConfirm={handleDecline}
                    >
                        <PoolTextArea
                            label={<FormattedMessage id="app.label.declineReason" />}
                            rows="5"
                            rowsMax="50"
                            value={declineReason}
                            onChange={e => setDeclineReason(e.target.value)}
                        />
                    </PoolConfirmationModal>
                </FormikGroupItem>
            </Stack>
        </PaloThemeProvider>
    );
};
